import { useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react"
import { AnimatePresence, motion } from "motion/react"
import { Check, ChevronDown } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"

// ============================================================================
// Primitives for the standalone activation surface. Quiet, professional
// wizard language on the app's own type system: Inter everywhere, Syne
// strictly for the wordmark. One column, everything sharing the same edges;
// matched rectangular buttons; sentence-case labels — nothing shouts.
// ============================================================================

export const INK = "#0C2018"

// Brand colors, sampled from the official eTX ImpaXt logo.
export const BRAND_PINE = "#185341" // the "eTX" green
export const BRAND_EMERALD = "#0FA36B" // brackets + tagline green
export const BRAND_CHARCOAL = "#2B2B2B" // "ImpaXt"

// The logo's corner brackets, reusable as a quiet brand motif: a viewfinder
// framing whatever matters most on the screen (the signature, the booked
// appointment). Parent must be `relative`.
export function CornerMarks({ size = 12 }: { size?: number }) {
  const base = "absolute border-[#0FA36B]"
  const s = { width: size, height: size }
  return (
    <>
      <span aria-hidden style={s} className={cx(base, "left-0 top-0 rounded-tl-[2px] border-l-2 border-t-2")} />
      <span aria-hidden style={s} className={cx(base, "right-0 top-0 rounded-tr-[2px] border-r-2 border-t-2")} />
      <span aria-hidden style={s} className={cx(base, "bottom-0 left-0 rounded-bl-[2px] border-b-2 border-l-2")} />
      <span aria-hidden style={s} className={cx(base, "bottom-0 right-0 rounded-br-[2px] border-b-2 border-r-2")} />
    </>
  )
}

// Quiet section label — sentence case, no tracking games.
export const sectionLabelClass = "text-[12.5px] font-semibold text-[#718077]"

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const focusRing =
  "outline-none focus-visible:ring-4 focus-visible:ring-emerald-500/25 focus-visible:border-emerald-500"

export function inputClass(invalid?: boolean) {
  return cx(
    "w-full h-11 rounded-lg border bg-white px-3.5 text-[14.5px] text-[#0C2018] placeholder:text-zinc-300 outline-none transition-all duration-150",
    invalid
      ? "border-red-400 bg-red-50/40 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
      : "border-[#D5DDD8] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10",
  )
}

// Text input with LIVE positive feedback: the moment the value is valid, a
// small emerald check springs in at the right edge. The form acknowledges
// every completed field — quiet delight, zero friction.
export function AInput({
  valid,
  invalid,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { valid?: boolean; invalid?: boolean }) {
  return (
    <div className="relative">
      <input
        {...props}
        aria-invalid={invalid || undefined}
        className={cx(inputClass(invalid), valid && "border-emerald-300 pr-10", className)}
      />
      <AnimatePresence>
        {valid && (
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0, transition: { duration: 0.12 } }}
            transition={{ type: "spring", stiffness: 500, damping: 24 }}
            className="pointer-events-none absolute right-3 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-full bg-emerald-100"
          >
            <Check className="size-3 text-emerald-600" strokeWidth={3} aria-hidden />
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}

// The logo's viewfinder, as an interaction: brackets converge from outside
// and lock onto the content with a soft emerald pulse. Used to "acquire" the
// shop's name on the cover.
export function ViewfinderTarget({ children }: { children: ReactNode }) {
  const corners = [
    { cls: "left-0 top-0 border-l-2 border-t-2 rounded-tl-[2px]", x: -16, y: -12 },
    { cls: "right-0 top-0 border-r-2 border-t-2 rounded-tr-[2px]", x: 16, y: -12 },
    { cls: "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-[2px]", x: -16, y: 12 },
    { cls: "bottom-0 right-0 border-b-2 border-r-2 rounded-br-[2px]", x: 16, y: 12 },
  ]
  return (
    <motion.span initial="out" animate="in" className="relative inline-flex items-center px-3 py-1.5">
      {corners.map((c) => (
        <motion.span
          key={c.cls}
          aria-hidden
          variants={{
            out: { opacity: 0, x: c.x, y: c.y },
            in: {
              opacity: 1,
              x: 0,
              y: 0,
              transition: { type: "spring", stiffness: 300, damping: 22, delay: 0.3 },
            },
          }}
          className={cx("absolute size-2.5 border-[#0FA36B]", c.cls)}
        />
      ))}
      {/* lock pulse once the brackets land */}
      <motion.span
        aria-hidden
        variants={{ out: { opacity: 0 }, in: { opacity: [0, 0.3, 0], transition: { delay: 0.75, duration: 0.55 } } }}
        className="absolute inset-0 rounded-md bg-emerald-400"
      />
      <motion.span variants={{ out: { opacity: 0 }, in: { opacity: 1, transition: { delay: 0.15 } } }}>
        {children}
      </motion.span>
    </motion.span>
  )
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string | false
  children: ReactNode
}) {
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-[13px] font-semibold text-[#33413A]">{label}</span>
        {hint && <span className="shrink-0 text-right text-[12px] leading-tight text-[#96A29A]">{hint}</span>}
      </div>
      {children}
      {error && (
        <p className="mt-1.5 text-[12.5px] font-medium text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

// Focus + scroll to the first invalid control after a failed Continue.
export function focusFirstInvalid() {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>('[aria-invalid="true"]')
    el?.scrollIntoView({ behavior: "smooth", block: "center" })
    el?.focus({ preventScroll: true })
  })
}

/* ── Step header — the card's voice ─────────────────────── */

export function StageHeader({
  stage,
  time,
  title,
  lede,
}: {
  stage?: string // e.g. "Step 2 of 5"
  time?: string // e.g. "about 30 seconds"
  title: string
  lede?: ReactNode
}) {
  return (
    <header className="mb-6">
      {stage && (
        <p className="mb-1.5 text-[12.5px] font-medium text-[#8A9890]">
          <span className="font-semibold text-emerald-700">{stage}</span>
          {time && <> · {time}</>}
        </p>
      )}
      <h1 className="text-[24px] font-bold leading-[1.15] tracking-tight text-[#0C2018] md:text-[26px]">
        {title}
      </h1>
      {lede && <p className="mt-2 max-w-lg text-[14.5px] leading-relaxed text-[#5A6A61]">{lede}</p>}
    </header>
  )
}

/* ── Buttons — matched heights, rectangular, quiet ──────── */

export function PrimaryButton({
  children,
  onClick,
  disabled,
  loading,
  type = "button",
  className,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  type?: "button" | "submit"
  className?: string
}) {
  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      whileTap={!disabled && !loading ? { scale: 0.98 } : undefined}
      className={cx(
        className,
        "inline-flex h-12 min-w-[150px] items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-emerald-500 to-emerald-600 px-7 text-[14.5px] font-semibold text-white transition-all duration-150",
        "shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_8px_20px_-8px_rgba(5,150,105,0.55)]",
        focusRing,
        "hover:from-emerald-500 hover:to-emerald-700 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_10px_24px_-8px_rgba(5,150,105,0.6)]",
        "disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:from-zinc-200 disabled:to-zinc-200 disabled:text-zinc-400 disabled:shadow-none",
      )}
    >
      {loading ? (
        <span className="size-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
      ) : (
        children
      )}
    </motion.button>
  )
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-[#D5DDD8] bg-white px-6 text-[14.5px] font-semibold text-[#33413A] transition-colors duration-150",
        focusRing,
        "hover:bg-[#F6F8F7] disabled:opacity-40",
      )}
    >
      {children}
    </button>
  )
}

// The step footer: Back on the left, the primary action on the right — both
// present on EVERY step, matched heights — with an optional helper line under
// the action. The sidebar's visited steps offer jump-back as a bonus, but the
// footer Back button is always there.
export function ActionRow({
  back,
  note,
  children,
}: {
  back?: () => void
  note?: string
  children: ReactNode
}) {
  return (
    <div className="mt-8 border-t border-[#EDF1EE] pt-5">
      <div className="flex items-center justify-between gap-4">
        {back ? <SecondaryButton onClick={back}>Back</SecondaryButton> : <span aria-hidden />}
        {children}
      </div>
      {note && (
        <p className="mt-2 text-right text-[12px] text-[#96A29A]" role="status">
          {note}
        </p>
      )}
    </div>
  )
}

/* ── Selection tiles (radios / checkboxes as clear targets) ─ */

export function SelectTile({
  selected,
  onClick,
  children,
  role = "radio",
  invalid,
}: {
  selected: boolean
  onClick: () => void
  children: ReactNode
  role?: "radio" | "checkbox"
  invalid?: boolean
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      aria-invalid={invalid || undefined}
      onClick={onClick}
      className={cx(
        "flex min-h-11 items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left text-[14px] font-medium transition-all duration-150",
        focusRing,
        selected
          ? "border-emerald-500 bg-emerald-50/60 font-semibold text-emerald-900 shadow-[0_2px_8px_-4px_rgba(5,150,105,0.35)]"
          : invalid
            ? "border-red-300 text-[#5A6A61] hover:border-red-400"
            : "border-[#D5DDD8] bg-white text-[#4A5A51] hover:-translate-y-px hover:border-emerald-300 hover:shadow-sm",
      )}
    >
      <motion.span
        key={selected ? "on" : "off"}
        initial={{ scale: selected ? 0.5 : 1 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 520, damping: 20 }}
        className={cx(
          "grid size-[18px] shrink-0 place-items-center border transition-colors duration-150",
          role === "checkbox" ? "rounded" : "rounded-full",
          selected ? "border-emerald-600 bg-emerald-600 text-white" : "border-[#C6D2CB] bg-white",
        )}
      >
        {selected && <Check className="size-3" strokeWidth={3.5} />}
      </motion.span>
      {children}
    </button>
  )
}

/* ── Select (custom, matches the input language) ────────── */

export function ASelect({
  value,
  onChange,
  options,
  placeholder,
  invalid,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder: string
  invalid?: boolean
}) {
  const [open, setOpen] = useState(false)
  // Opens upward when there isn't room below the trigger — long lists (the
  // 11-option BMS one) otherwise run off the bottom of the viewport.
  const [drop, setDrop] = useState<"down" | "up">("down")
  const rootRef = useRef<HTMLDivElement>(null)

  const LIST_MAX = 256 // px — keep in sync with max-h-64 below

  const toggle = () => {
    const r = rootRef.current?.getBoundingClientRect()
    if (r) {
      const spaceBelow = window.innerHeight - r.bottom
      setDrop(spaceBelow < LIST_MAX + 16 && r.top > spaceBelow ? "up" : "down")
    }
    setOpen((v) => !v)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        onClick={toggle}
        className={cx(inputClass(invalid), focusRing, "flex items-center justify-between gap-3 text-left")}
      >
        <span className={cx("truncate", !value && "text-zinc-500")}>{value || placeholder}</span>
        <ChevronDown
          className={cx("size-4 shrink-0 text-[#8A9890] transition-transform duration-150", open && "rotate-180")}
          aria-hidden
        />
      </button>
      {open && (
        <ul
          role="listbox"
          className={cx(
            "absolute z-20 max-h-64 w-full overflow-y-auto rounded-lg border border-[#D5DDD8] bg-white py-1 shadow-lg shadow-[#0C2018]/[0.08]",
            drop === "up" ? "bottom-full mb-1.5" : "top-full mt-1.5",
          )}
        >
          {options.map((o) => (
            <li key={o}>
              <button
                type="button"
                role="option"
                aria-selected={o === value}
                onClick={() => {
                  onChange(o)
                  setOpen(false)
                }}
                className={cx(
                  "flex h-10 w-full items-center justify-between px-3.5 text-left text-[14px] transition-colors",
                  focusRing,
                  o === value ? "font-semibold text-emerald-700" : "text-[#33413A] hover:bg-[#F2F5F3]",
                )}
              >
                {o}
                {o === value && <Check className="size-4 shrink-0 text-emerald-600" aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* ── Hairline rows (receipts on the done screen) ────────── */

export function HairlineRows({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-[#EDF1EE] border-y border-[#EDF1EE]">{children}</div>
}
